import type {
  PartnerAddressV2,
  PartnerCarrierAccountV2,
  PartnerContact,
  PartnerTenant,
} from '@shipium-corp/shipium-ts-types';
import type { CarrierAccount } from '../../assets/types';
import type { HydratedCarrierAccount, HydratedContact, HydrationAssetError } from '../types';
import { hydrateAddress } from './addressHydration';
import { hydrateGenericBaseAsset } from './hydrateGenericBaseAssest';
import { extractTenantDetails, findScopedTenant, generateHydrationError, isActiveAsset } from './utils';
const carrierAccountAssetType = 'carrierAccount';
const potentialAccountAddresses: (keyof CarrierAccount)[] = [
  'shipperAddressId',
  'sortFacilityAddressId',
  'returnToAddressId',
  'unitedStatesPrincipalPartyInInterestAddressId',
];

const getAddressDetails = (
  tenants: PartnerTenant[],
  carrierAccount: PartnerCarrierAccountV2,
  aMap: Record<string, PartnerAddressV2>,
) => {
  const addressObject: Record<string, ReturnType<typeof hydrateAddress>> = {};
  for (const idType of potentialAccountAddresses) {
    // relies on convention of removing "id" from suffix
    const hydrated = hydrateAddress(tenants, aMap[carrierAccount[idType] as string]);
    if (hydrated.validationInfo.errors.find((e: HydrationAssetError) => e.error === 'Not configured')) {
      continue;
    }
    addressObject[idType.slice(0, idType.length - 2)] = hydrated;
  }
  return addressObject;
};

const potentialAccountContact: (keyof CarrierAccount)[] = [
  'shipperContactId',
  'sortFacilityContactId',
  'returnToContactId',
  'unitedStatesPrincipalPartyInInterestContactId',
];
const getContactDetails = (
  tenants: PartnerTenant[],
  carrierAccount: PartnerCarrierAccountV2,
  cMap: Record<string, PartnerContact>,
) => {
  const contactObject: Record<string, HydratedContact> = {};
  for (const idType of potentialAccountContact) {
    const hydrated: HydratedContact = hydrateGenericBaseAsset(
      tenants,
      'contacts',
      cMap[carrierAccount[idType] as string],
      cMap[carrierAccount[idType] as string]?.contactId,
    );
    if (hydrated.validationInfo.errors.find((e) => e.error === 'Not configured')) {
      continue;
    }
    contactObject[idType.slice(0, idType.length - 2)] = hydrated;
  }
  return contactObject;
};
export const hydrateCarrierAccount = (
  tenantList: PartnerTenant[],
  contacts: Record<string, PartnerContact>,
  addresses: Record<string, PartnerAddressV2>,
  carrierAccount?: PartnerCarrierAccountV2,
): HydratedCarrierAccount => {
  const notActive =
    !isActiveAsset(carrierAccount) && carrierAccount
      ? generateHydrationError('Not active', carrierAccountAssetType)
      : null;
  const notLinked = !carrierAccount ? generateHydrationError('Not configured', carrierAccountAssetType) : null;
  const tenant = carrierAccount
    ? findScopedTenant(carrierAccount?.carrierAccountId!, 'carrierAccounts', tenantList)
    : null;
  // TODO figured out what needs to be linked
  const errors: HydrationAssetError[] = [notActive, notLinked].filter((err): err is HydrationAssetError => !!err);

  return {
    ...(carrierAccount ?? ({} as any)),
    ...(carrierAccount ? getAddressDetails(tenantList, carrierAccount, addresses) : {}),
    ...(carrierAccount ? getContactDetails(tenantList, carrierAccount, contacts) : {}),
    ...(tenant ? { tenant: extractTenantDetails(tenant) } : {}),
    validationInfo: {
      errors,
      configurationValid: !errors.length,
      warnings: [],
      configurationComplete: !errors.length,
    },
  };
};
